import { useAuthContext } from "@dewo/app/contexts/AuthContext";
import { ThreepidSource } from "@dewo/app/graphql/types";
import {
  Alert,
  Button,
  Col,
  Divider,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import * as Colors from "@ant-design/colors";
import React, { FC, useMemo } from "react";
import { MetamaskAuthButton } from "../auth/buttons/MetamaskAuthButton";
import { PhantomAuthButton } from "../auth/buttons/PhantomAuthButton";
import {
  getThreepidName,
  renderThreepidIcon,
  ThreepidAuthButton,
} from "../auth/buttons/ThreepidAuthButton";
import { useDeleteThreepid } from "../auth/hooks";
import { useRunningCallback } from "@dewo/app/util/hooks";
import { UserSkillSelect } from "./UserSkillSelect";
import { WalletConnectAuthButton } from "../auth/buttons/WalletConnectAuthButton";
import _ from "lodash";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";

interface Props {}

export const UserSettings: FC<Props> = () => {
  const { user } = useAuthContext();
  // Some keys of ThreepidSource are in authMap
  const authComponentMap: Partial<Record<ThreepidSource, FC>> = {
    [ThreepidSource.metamask]: MetamaskAuthButton,
    [ThreepidSource.phantom]: PhantomAuthButton,
  };

  function Dynamic<T>({ is: IsComponent, props }: { is: FC<T>; props?: T }) {
    return <IsComponent {...(props as T)} />;
  }

  const deleteThreepid = useDeleteThreepid();
  const [handleDeleteThreepid, deletingThreepid] = useRunningCallback(
    deleteThreepid,
    [deleteThreepid]
  );

  const threepidBySource = useMemo(
    () => _.keyBy(user?.threepids, "source"),
    [user?.threepids]
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Col>
        <Typography.Title level={5}>Skills</Typography.Title>
        <Typography.Paragraph type="secondary">
          Select your skills. This is used to show you relevant tasks and find
          interesting workspaces.
        </Typography.Paragraph>
        <UserSkillSelect
          placeholder="Select skills..."
          style={{ width: "100%" }}
        />
        <Divider />
        <Typography.Title level={5}>Connected Accounts</Typography.Title>
        <Space direction="vertical">
          {[
            ThreepidSource.github,
            ThreepidSource.discord,
            ThreepidSource.phantom,
            ThreepidSource.metamask,
          ].map((source) => {
            const threepid = threepidBySource[source];
            if (!!user && !!threepid) {
              return (
                <Alert
                  key={source}
                  message={`Connected with ${getThreepidName[source]}`}
                  icon={renderThreepidIcon[source]}
                  type="success"
                  showIcon
                  action={
                    user.threepids.length > 1 && (
                      <Popconfirm
                        icon={
                          <DeleteOutlined
                            style={{ color: Colors.grey.primary }}
                          />
                        }
                        title={`Disconnect ${getThreepidName[source]}?`}
                        okType="danger"
                        okText="Disconnect"
                        onConfirm={() => handleDeleteThreepid(threepid.id)}
                      >
                        <Button
                          size="small"
                          type="text"
                          className="text-secondary"
                          loading={deletingThreepid}
                          icon={<CloseOutlined />}
                        />
                      </Popconfirm>
                    )
                  }
                />
              );
            }

            if (!!authComponentMap[source]) {
              return <Dynamic is={authComponentMap[source]!} key={source} />;
            }

            return (
              <ThreepidAuthButton
                key={source}
                source={source}
                children={`Connect with ${getThreepidName[source]}`}
              />
            );
          })}
          {!threepidBySource[ThreepidSource.metamask] && (
            <WalletConnectAuthButton children="Connect with WalletConnect" />
          )}
        </Space>
      </Col>
    </Space>
  );
};
